import { useMemo, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import Logo from "../components/Logo";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot

  const platform = useMemo(() => detectPlatform(), []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Accesso effettuato");
        nav("/", { replace: true });
      } else {
        await api.post("/auth/forgot-password", { email });
        toast.success("Se l'indirizzo è registrato riceverai un'email con le istruzioni.");
        setMode("login");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F9FC] px-4 py-8">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex flex-col items-center">
            <Logo className="h-24 w-auto sm:h-28" />
          </div>

          <h1 className="text-xl font-bold tracking-tight text-[#1F2937] sm:text-2xl">
            {mode === "login" ? "Accedi alla tua area" : "Password dimenticata"}
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {mode === "login"
              ? "Area riservata agli ISF Maven Pharma."
              : "Inserisci l'email associata al tuo account."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-xs font-semibold text-[#1F2937]">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="maven-input"
                placeholder="tu@mavenacademy.it"
                data-testid="login-email-input"
              />
            </div>

            {mode === "login" && (
              <div>
                <label htmlFor="password" className="mb-1 block text-xs font-semibold text-[#1F2937]">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="maven-input"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="maven-btn-primary w-full"
              data-testid="login-submit-button"
            >
              {busy ? "Attendi..." : mode === "login" ? "Accedi" : "Invia link di reset"}
            </button>

            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => setMode(mode === "login" ? "forgot" : "login")}
                className="text-xs font-semibold text-[#0153A3] hover:underline"
                data-testid="toggle-forgot-mode"
              >
                {mode === "login" ? "Password dimenticata?" : "Torna al login"}
              </button>
            </div>
          </form>
        </div>

        {/* PWA install hint - visually secondary */}
        <div className="mt-4 rounded-xl border border-[#E5E7EB] bg-white/60 p-4" data-testid="pwa-install-hint">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-[#1F2937]">
            <span>Aggiungi Maven Academy alla schermata</span>
            <span className="inline-flex items-center gap-1">
              Home <Smartphone size={13} className="text-[#0153A3]" aria-hidden="true" />
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className={`rounded-lg border border-[#E5E7EB] p-2.5 ${platform === "ios" ? "bg-[#F7F9FC]" : ""}`}>
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[#0153A3]">
                iOS · Safari
              </div>
              <ol className="ml-4 list-decimal space-y-0.5 text-[11px] text-[#6B7280]">
                <li>Tocca il pulsante Condividi</li>
                <li>Seleziona "Aggiungi alla schermata Home"</li>
                <li>Tocca "Aggiungi"</li>
              </ol>
            </div>
            <div className={`rounded-lg border border-[#E5E7EB] p-2.5 ${platform === "android" ? "bg-[#F7F9FC]" : ""}`}>
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-[#0153A3]">
                Android · Chrome
              </div>
              <ol className="ml-4 list-decimal space-y-0.5 text-[11px] text-[#6B7280]">
                <li>Apri il menu ⋮</li>
                <li>Tocca "Aggiungi a schermata Home" o "Installa app"</li>
                <li>Conferma</li>
              </ol>
            </div>
          </div>
        </div>

        <footer className="mt-4 text-center text-xs text-[#9CA3AF]" data-testid="app-footer">
          © 2026 Maven Pharma S.r.l. ·{" "}
          <Link to="/privacy-policy" className="font-semibold text-[#0153A3] hover:underline" data-testid="footer-privacy-link">
            Informativa Privacy
          </Link>
        </footer>
      </div>
    </div>
  );
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "";
}
